import React, { useMemo, useEffect, useCallback, useReducer } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import styled from 'styled-components';
import { Alert as AlertIcon } from 'grommet-icons/icons';
import { Props } from './types';
import { Text } from '../../components/Text';
import { web3ReactInterface } from '../ConnectWallet';
import WalletConnectModal from './components/WalletConnectModal';
import ValidatorTable from './components/ValidatorTable';
import Spinner from '../../components/Spinner';
import { PageTemplate } from '../../components/PageTemplate';
import {
  PRICE_PER_VALIDATOR,
  EJECTION_PRICE,
  TICKER_NAME,
} from '../../utils/envVars';
import { AllowedNetworks, NetworkChainId } from '../ConnectWallet/web3Utils';
import { Alert } from '../../components/Alert';
import { Link } from '../../components/Link';
import { Button } from '../../components/Button';
import {
  sendConsolidationTransactions,
  eligibleForTx,
} from './components/ConsolidationTransactions';
import { getValidators } from './components/GetValidators';
import { consolidationReducer } from '../../store/reducers/consolidate';
import { Heading } from '../../components/Heading';

const SubTextContainer = styled.div``;

const FakeLink = styled.span`
  color: blue;
  text-decoration: underline;
  cursor: pointer;
  display: inline;
`;

const ButtonLink = styled(FakeLink)`
  text-decoration: none;
  width: fit-content;
  button {
    padding: 16px 32px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  @media screen and (max-width: 518px) {
    span,
    button {
      width: 100%;
    }
  }
`;

const _ConsolidatePage: React.FC<Props> = () => {
  const {
    account,
    active,
    chainId,
    deactivate,
    connector,
  }: web3ReactInterface = useWeb3React<Web3Provider>();
  const [validators, dispatch] = useReducer(consolidationReducer, {
    keys: [],
    isTargetSelected: false,
  });
  // const [validators, setValidators] = useState<ConsolidationKeyInterface[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [validatorLoadError, setValidatorLoadError] = React.useState<boolean>(
    false
  );

  const { formatMessage } = useIntl();

  // an effect that fetches validators from beaconchain when the user connects or changes their wallet
  useEffect(() => {
    const network = NetworkChainId[chainId as number];

    const isValidNetwork = Object.values(AllowedNetworks).includes(network);

    if (active && account && isValidNetwork) {
      getValidators(account, setLoading, dispatch, setValidatorLoadError);
    }
  }, [account, active, chainId]);

  const handleConnect = useCallback((): void => {
    setLoading(true);
    deactivate();
  }, [setLoading, deactivate]);

  const handleModalClose = useCallback((): void => {
    setLoading(false);
  }, [setLoading]);

  const topUpPageContent = useMemo(() => {
    if (loading) {
      return <Spinner className="mt40" />;
    }

    if (!active) {
      return (
        <ButtonContainer>
          <ButtonLink onClick={handleConnect}>
            <Button
              rainbow
              label={formatMessage({ defaultMessage: 'Connect wallet' })}
            />
          </ButtonLink>
        </ButtonContainer>
      );
    }
    if (validatorLoadError) {
      return (
        <Alert variant="warning" className="my10">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <AlertIcon color="redLight" />
            <Text className="ml10">
              <FormattedMessage defaultMessage="There was an error loading your validator information from Beaconcha.in" />
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Text className="mt20">
              <FormattedMessage
                defaultMessage="You can {reloadYourWallet} to try again, or refresh the page. If issue persists, let us know on {github}."
                values={{
                  reloadYourWallet: (
                    <FakeLink onClick={deactivate}>
                      <FormattedMessage defaultMessage="reload your wallet" />
                    </FakeLink>
                  ),
                  github: (
                    <Link
                      inline
                      primary
                      to="https://github.com/lukso-network/network-deposit-launchpad/issues/new"
                    >
                      GitHub
                    </Link>
                  ),
                }}
                description="{reloadYourWallet} is a link labeled 'reload your wallet'"
              />
            </Text>
          </div>
        </Alert>
      );
    }

    const eligibleCount = eligibleForTx(validators.keys).length;
    return (
      <>
        {validators.keys.length > 0 && (
          <ButtonContainer>
            <Button
              label={`Send ${eligibleCount} transaction${
                eligibleCount !== 1 ? 's' : ''
              }`}
              disabled={eligibleCount < 1 || !validators.isTargetSelected}
              onClick={() =>
                sendConsolidationTransactions(
                  account,
                  connector,
                  validators.keys,
                  dispatch
                )
              }
            />
          </ButtonContainer>
        )}
        <ValidatorTable
          validators={validators.keys}
          dispatch={dispatch}
          handleConnect={handleConnect}
        />
      </>
    );
  }, [
    account,
    connector,
    loading,
    validatorLoadError,
    active,
    validators,
    deactivate,
    formatMessage,
    handleConnect,
  ]);

  return (
    <>
      {/* the wallet connect modal controls it's own display, so it is always rendered here */}
      <WalletConnectModal
        loading={loading}
        handleModalClose={handleModalClose}
      />

      <PageTemplate
        title={formatMessage({ defaultMessage: 'Consolidate validators' })}
      >
        <SubTextContainer className="mt20">
          <Heading level={3}>
            <FormattedMessage defaultMessage="Managing your validators" />
          </Heading>
          <Text className="mt10">
            <FormattedMessage
              defaultMessage="Pectra fork introduces a new way to manage your validators - a validator consolidation."
              values={{ PRICE_PER_VALIDATOR, TICKER_NAME, EJECTION_PRICE }}
            />
          </Text>
          <Text className="mt10">
            <FormattedMessage
              defaultMessage="The consolidation lets you stake more {TICKER_NAME} per single validator key. It also lets you consolidate multiple validators into a single one."
              values={{ PRICE_PER_VALIDATOR, TICKER_NAME, EJECTION_PRICE }}
            />
          </Text>
          <Alert variant="info" className="my20">
            <Link
              inline
              primary
              to="https://github.com/ethstaker/ethstaker-guides/blob/main/docs/pectra-features.md#rewards-and-proposer-selection-probability"
            >
              <FormattedMessage defaultMessage="More on EIP-7251 and validator consolidation" />
            </Link>
          </Alert>
        </SubTextContainer>

        {/* the main content for the topup page */}
        {topUpPageContent}
      </PageTemplate>
    </>
  );
};

export const ConsolidatePage = _ConsolidatePage;
